"use client";

import { useCallback, useMemo } from "react";

import { useLocalStorage } from "@uidotdev/usehooks";

import type { SavedPlottingFilters } from "@/providers/maps/contexts/plotting-filters-context.types";
import { LocalStorageKeys } from "@/shared/enums/local-storage-keys.enum";

export type SetSavedFilters = React.Dispatch<
  React.SetStateAction<Record<string, SavedPlottingFilters> | null>
>;

export interface UsePlottingFiltersStorageResult {
  currentDistrictFilters: SavedPlottingFilters | null;
  savedFilters: Record<string, SavedPlottingFilters> | null;
  setSavedFilters: SetSavedFilters;
  saveFiltersForDistrict: (districtId: string, entry: SavedPlottingFilters) => void;
}

export function usePlottingFiltersStorage(
  selectedDistrictId: string | null,
  isLoadingMetadata: boolean,
  fulfilledTimeStamp: number | undefined,
): UsePlottingFiltersStorageResult {
  const [savedFilters, setSavedFilters] =
    useLocalStorage<Record<string, SavedPlottingFilters> | null>(
      LocalStorageKeys.PlottingFilters,
      null,
    );

  const currentDistrictFilters = useMemo(() => {
    if (!selectedDistrictId || !savedFilters || isLoadingMetadata || !fulfilledTimeStamp) return null;
    return savedFilters[selectedDistrictId] ?? null;
  }, [selectedDistrictId, savedFilters, isLoadingMetadata, fulfilledTimeStamp]);

  const saveFiltersForDistrict = useCallback(
    (districtId: string, entry: SavedPlottingFilters) => {
      setSavedFilters((prev) => ({
        ...(prev ?? {}),
        [districtId]: entry,
      }));
    },
    [setSavedFilters],
  );

  return { currentDistrictFilters, savedFilters, setSavedFilters, saveFiltersForDistrict };
}
